import { useSelector } from 'react-redux';
import { RootState } from '../store';

export type PermissionAction =
  | 'view' | 'create' | 'edit' | 'delete'
  | 'approve' | 'reject' | 'assign'
  | 'export' | 'import' | 'print'
  | 'upload' | 'download'
  | 'verify' | 'cancel' | 'restore' | 'override'
  | 'manage_settings'
  // Scoped view actions used by the backend_support_* modules (see holdsAnyView below). In the
  // TYPE only, deliberately not in ALL_ACTIONS — that array drives the permission-matrix grid,
  // and these scopes are configured per-module there, not as universal columns.
  | 'view_own' | 'view_all' | 'view_team' | 'reassign' | 'close';

export const ALL_ACTIONS: PermissionAction[] = [
  'view', 'create', 'edit', 'delete',
  'approve', 'reject', 'assign',
  'export', 'import', 'print',
  'upload', 'download',
  'verify', 'cancel', 'restore', 'override',
  'manage_settings',
];

export const MODULES = [
  // HR modules
  'dashboard', 'employees', 'departments', 'recruitment', 'attendance',
  'shifts', 'leaves', 'payroll', 'performance', 'training', 'expenses',
  'helpdesk', 'analytics', 'settings', 'reports', 'hr_documents',
  // ERP / Telematics modules
  // NOTE: `assets` lives here (ERP Inventory), not HR — asset allocation is an inventory concern.
  // The key stays `assets` (the backend guards @RequirePermission('assets', …)); only its grouping moved.
  'assets',
  'erp_dashboard', 'erp_devices', 'erp_sims', 'erp_products', 'erp_vendors',
  'erp_purchase_orders', 'erp_grn', 'erp_branch_receipts', 'erp_warehouses', 'erp_branches',
  'erp_warehouse_stock', 'erp_branch_stock', 'erp_technician_stock',
  'erp_stock_requests', 'erp_branch_asset_requests', 'erp_transfers', 'erp_installations', 'erp_installation_verification', 'erp_installation_payment', 'erp_vehicle_mapping',
  'erp_warranty', 'erp_repairs', 'erp_replacements', 'erp_scrap', 'erp_audits',
  'erp_reports', 'erp_opening_stock', 'erp_stock_adjustment', 'erp_stock_corrections',
  // Payments report (Phase B3, 12 Oct 2026, user) — branch-walled on the server.
  'erp_payments_report',
  // Reserved devices register (Phase 1, 13 Oct 2026, user) — branch-walled on the server.
  'erp_reserved_devices',
  // Branch Manager modules
  'erp_bm_dashboard', 'erp_bm_stock', 'erp_bm_stock_requests', 'erp_bm_transfer_requests',
  'erp_bm_technician_issue', 'erp_bm_technician_return', 'erp_bm_installations',
  'erp_bm_installed_devices', 'erp_bm_vehicle_mapping', 'erp_bm_warranty',
  'erp_bm_replacements', 'erp_bm_repair_requests', 'erp_bm_support_requests', 'erp_bm_expenses',
  'erp_bm_customer_outstanding', 'erp_bm_reports', 'erp_bm_imei_search',
  'erp_bm_missing_devices', 'erp_bm_reconciliation', 'erp_bm_stock_aging', 'erp_bm_receipts',
  // CRM modules
  'crm_dashboard', 'crm_tickets', 'crm_customers', 'crm_clients', 'crm_companies', 'crm_vehicles', 'crm_pricing', 'crm_sla', 'crm_sla_config',
  'crm_escalations', 'crm_technician_jobs', 'crm_warranty', 'crm_rma',
  'crm_replacements', 'crm_followups', 'crm_communication', 'crm_reports', 'crm_sla_reports',
  // Technician Management modules
  'tm_dashboard', 'tm_jobs', 'tm_attendance', 'tm_location', 'tm_payments',
  'tm_payments_verify', 'tm_stock', 'tm_stock_requests', 'tm_support_requests',
  'tm_reports', 'tm_manager', 'tm_live_tracking', 'tm_profiles', 'tm_reconciliation',
  // Technical Support modules
  'ts_dashboard', 'ts_tickets', 'ts_diagnostics', 'ts_customer360',
  'ts_sla_breach', 'ts_settings', 'ts_escalation',
  // Backend Support modules
  'backend_support_work', 'backend_support_ticket',
  'backend_support_performance', 'backend_support_config',
  'w4t_online_companies', 'w4t_online_device_types',
  // Sales & Marketing modules
  'sm_dashboard_sales', 'sm_dashboard_marketing',
  'sm_leads', 'sm_leads_create', 'sm_leads_assign',
  'sm_followups', 'sm_demos', 'sm_visits',
  'sm_quotations', 'sm_discount_approvals',
  'sm_targets', 'sm_campaigns',
  'sm_dealers', 'sm_collections',
  'sm_renewals', 'sm_reports',
  'sm_payment_followups', 'sm_expense_claims', 'sm_sales_attendance', 'sm_lost_leads',
  'sm_pipeline_kanban', 'sm_executive_performance', 'sm_subscription_billing',
  // Finance & Accounts modules
  'fa_dashboard', 'fa_invoices', 'fa_purchase_invoices', 'fa_grn_verification',
  'fa_payments', 'fa_balance_payments', 'fa_technician_collections',
  'fa_ledger', 'fa_expenses', 'fa_petty_cash',
  'fa_credit_notes', 'fa_debit_notes', 'fa_delivery_challans', 'fa_imei_costs',
  'fa_profit_loss', 'fa_reports', 'fa_settings', 'fa_outstanding_payables',
  // Audit Log
  'audit_log',
  // Super Admin Control Center
  'super_admin_dashboard', 'super_admin_permissions', 'super_admin_staff_access',
  'super_admin_approvals', 'super_admin_modules', 'super_admin_settings',
] as const;

export type PermissionModule = typeof MODULES[number];

// Role → module-name prefix it owns by default.
// A user with this role always passes canView/can for matching modules,
// even with no designation assigned. Designation permissions are additive on top.
//
// NOTE: `erp_admin` (user-facing "Inventory Admin") is intentionally NOT given a
// prefix here. The old `erp_admin: 'erp_'` shortcut was unsafe — it auto-granted
// every `erp_*` module AND accidentally the Branch-Manager `erp_bm_*` set. Inventory
// Admin access is now fully permission-based (designation + staff overrides resolved
// by the backend), so it flows through the `user.permissions` check below. Branch
// Manager keeps its own-branch `erp_bm_` prefix.
const ROLE_MODULE_PREFIXES: Partial<Record<string, string>> = {
  branch_manager: 'erp_bm_',
};

// Designation name → module prefix for roles that don't have a dedicated system role
// (e.g. technicians use 'employee' role but need tm_* access).
const DESIGNATION_MODULE_PREFIXES: Array<{ match: (n: string) => boolean; prefix: string }> = [
  {
    match: n => (n.includes('technician') || n.includes('field_tech')) &&
                !n.includes('technical_support') && !n.includes('tech_support'),
    prefix: 'tm_',
  },
];

const normDes = (s: string) => (s || '').toLowerCase().replace(/[\s\-]+/g, '_');

const usePermission = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const workspace = useSelector((state: RootState) => state.workspace);

  const isSuperAdmin = user?.role === 'super_admin';
  const isWorkspaceActive = isSuperAdmin && workspace.active;

  // Operations Manager — one notch below Super Admin: full access to every
  // operational module, but NOT the Super Admin Control Center (`super_admin_*`
  // modules — permission/staff-access management, system settings, module toggles).
  // Mirrors the backend RolesGuard / EffectivePermissionService carve-out.
  const isOperationsManager = user?.role === 'operations_manager';
  const isControlCenter = (module: string) => module.startsWith('super_admin_');

  // Identity-based self-service access: any authenticated user who is linked to an
  // employee record (has employeeId) can see their own self-service pages
  // (My Workspace). This is intentionally independent of designation/module
  // permissions like `attendance:view`.
  const hasEmployeeProfile = !!user?.employeeId;

  const desName = normDes(user?.designationName ?? '');
  const desPrefix = DESIGNATION_MODULE_PREFIXES.find(d => d.match(desName))?.prefix;
  /**
   * Actually a FIELD technician (by designation), as opposed to someone merely granted tm_jobs
   * read access (Backend Support reads technician jobs for service history — that must not put
   * "My Jobs" in their sidebar; a desk agent has no field jobs of their own).
   */
  const isFieldTechnician = !!desPrefix;

  // Full permission check — super admin ALWAYS has full access regardless of workspace
  const can = (module: PermissionModule | string, action: PermissionAction): boolean => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    if (isOperationsManager) return !isControlCenter(module);
    const rolePrefix = ROLE_MODULE_PREFIXES[user.role as string];
    if (rolePrefix && module.startsWith(rolePrefix)) return true;
    if (desPrefix && module.startsWith(desPrefix)) return true;
    return (user.permissions || []).includes(`${module}:${action}`);
  };

  /**
   * Any action that means "you can see this module".
   *
   * Most modules grant a plain `view`. The backend_support_* modules deliberately do not — they
   * split it by scope (`view_own` vs `view_all`, and `view_team` on performance) because seeing
   * your own queue and seeing everyone's are different authorities.
   *
   * Checking only for `view` therefore hid the entire Backend Support console — sidebar entries AND
   * the routes, since PermissionRoute uses this too — from the very people whose designation grants
   * them the module. Holding any scope of view means the module is viewable; WHICH records they see
   * is enforced by the server, which is the right place for it.
   */
  const VIEW_ACTIONS = ['view', 'view_all', 'view_own', 'view_team'];
  const holdsAnyView = (perms: string[], module: string): boolean =>
    VIEW_ACTIONS.some((a) => perms.includes(`${module}:${a}`));

  // View check for sidebar filtering — in workspace mode, reflects workspace permissions
  const canView = (module: PermissionModule | string): boolean => {
    if (!user) return false;
    if (isSuperAdmin) {
      if (isWorkspaceActive) {
        return holdsAnyView(workspace.permissions, module);
      }
      return true;
    }
    if (isOperationsManager) return !isControlCenter(module);
    const rolePrefix = ROLE_MODULE_PREFIXES[user.role as string];
    if (rolePrefix && module.startsWith(rolePrefix)) return true;
    if (desPrefix && module.startsWith(desPrefix)) return true;
    return holdsAnyView(user.permissions || [], module);
  };

  // Original 7 actions
  const canCreate  = (module: PermissionModule | string) => can(module, 'create');
  const canEdit    = (module: PermissionModule | string) => can(module, 'edit');
  const canDelete  = (module: PermissionModule | string) => can(module, 'delete');
  const canApprove = (module: PermissionModule | string) => can(module, 'approve');
  const canExport  = (module: PermissionModule | string) => can(module, 'export');
  const canPrint   = (module: PermissionModule | string) => can(module, 'print');

  // 10 new actions (Phase A)
  const canReject         = (module: PermissionModule | string) => can(module, 'reject');
  const canAssign         = (module: PermissionModule | string) => can(module, 'assign');
  const canImport         = (module: PermissionModule | string) => can(module, 'import');
  const canUpload         = (module: PermissionModule | string) => can(module, 'upload');
  const canDownload       = (module: PermissionModule | string) => can(module, 'download');
  const canVerify         = (module: PermissionModule | string) => can(module, 'verify');
  const canCancel         = (module: PermissionModule | string) => can(module, 'cancel');
  const canRestore        = (module: PermissionModule | string) => can(module, 'restore');
  const canOverride       = (module: PermissionModule | string) => can(module, 'override');
  const canManageSettings = (module: PermissionModule | string) => can(module, 'manage_settings');

  return {
    can,
    canView, canCreate, canEdit, canDelete,
    canApprove, canReject, canAssign,
    canExport, canImport, canPrint,
    canUpload, canDownload,
    canVerify, canCancel, canRestore, canOverride,
    canManageSettings,
    isSuperAdmin, isOperationsManager, isWorkspaceActive,
    isFieldTechnician,
    hasEmployeeProfile,
    employeeId: user?.employeeId ?? null,
    workspaceName: workspace.designationName,
    workspacePermissions: workspace.permissions,
  };
};

export default usePermission;
